import {Button, Col, Modal, Row, Space} from "antd";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useCreateKycContext} from "../../context/create-kyc";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";
import {CreateKycDocUploadFormField} from "./file-upload-field";
import {
  createKycDocumentUploadStepFormValidationSchema,
  CreateKycDocumentUploadStepFormValues,
} from "./schema-validation";
import {CreateKycDocUploadConfirmAccuracyFormField} from "./confirm-accuracy";
import {useState} from "react";
import {StyledModalContentWrapper} from "../../../../../components/calls/upcoming-calls/call-card/call-card.style";
import {
  StyledDocUploadVideoWrapper,
  StyledKycStatusRiveWrapper,
} from "./file-upload-field/styles";
import {useGetKycStatusContext} from "../../../get-kyc-status/context/get-kyc-status";
import {useQueryClient} from "@tanstack/react-query";
import {useRive, Layout, Fit, Alignment} from "@rive-app/react-canvas";
import {KYC_QUERY_KEYS, useUploadKycDocsOnly} from "../../../../../features/kyc";

export function CreateKycDocumentUploadStep() {
  const queryClient = useQueryClient();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const {dispatch} = useCreateKycContext();
  const {state: kycStatusState} = useGetKycStatusContext();

  const formMethods = useForm<CreateKycDocumentUploadStepFormValues>({
    resolver: yupResolver(createKycDocumentUploadStepFormValidationSchema),
    mode: "onChange",
    defaultValues: {
      confirmAccuracy: false,
    },
  });

  const {
    handleSubmit,
    formState: {errors},
    watch,
  } = formMethods;

  // Upload documents only (KYC record already created in Step 1)
  const uploadDocsOnly = useUploadKycDocsOnly();

  const onSubmit = async (data: CreateKycDocumentUploadStepFormValues) => {
    dispatch({type: "update-document-upload-form-data", payload: data});

    try {
      await uploadDocsOnly.upload({documentFormData: data});
      setIsSuccessModalOpen(true);
    } catch (err) {
      // Error already shown by hook via message.error
    }
  };

  function handleCloseSuccessModal() {
    queryClient
      .invalidateQueries({queryKey: ["kyc-status"]})
      .then(() => queryClient.invalidateQueries({queryKey: KYC_QUERY_KEYS.status()}))
      .then((v) => {
        setIsSuccessModalOpen(false);
        kycStatusState.setKycStatusOverride(false);
      });
  }

  const isSubmitting = uploadDocsOnly.isUploading;
  const {RiveComponent} = useRive({
    src: "/kyc_assets.riv",
    // animations: ["play"],
    layout: new Layout({
      fit: Fit.Contain, // Change to: rive.Fit.Contain, or Cover
      alignment: Alignment.Center,
    }),
    autoplay: true,
    artboard: "Shield with check mark",
    stateMachines: "State Machine 1",
  });
  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Space direction="vertical" size={24} style={{display: "flex"}}>
          <CreateKycDocUploadFormField
            defaultPreviewUrl={kycStatusState.kycStatusData?.data.proofOfAddressUrl}
            name="proofOfAddress"
            label="Proof of residential address"
            btnText="Upload a document"
            docFeedback="Acceptable documents include: utility bill (electricity, water, gas), bank statement, lease agreement, or official government letter (dated within the last 3 months)."
            uploadInstruction="We will need a recent document that clearly shows your full name and current residential address"
            modalTitle="Proof of residential address"
            videoSrc={"PoR"}
            modalUploadInstruction={
              <ul style={{marginBottom: 0, paddingInlineStart: "2.4rem"}}>
                <li>
                  Acceptable documents include:{" "}
                  <b>
                    utility bill (electricity, water, gas), bank statement, lease
                    agreement
                  </b>
                  , or <b>official government letter (dated within the last 3 months)</b>
                </li>
                <li>Ensure the document shows your full name and residential address</li>
                <li>Take the photo in a well-lit area with no glare or shadows</li>
                <li>Avoid blurry and unclear images</li>
              </ul>
            }
            required
          />
          <CreateKycDocUploadFormField
            defaultPreviewUrl={kycStatusState.kycStatusData?.data.governmentIdUrl}
            name="governmentId"
            label="Government-issued ID document"
            btnText="Upload a document"
            docFeedback="Names on verification documents must match your legal name. Acceptable documents include: valid government issued IDs such as a driver’s license, National Identity Card, or an International Passport."
            uploadInstruction="We will verify your identity using a verification document issued by your country."
            modalTitle="Government-issued ID document"
            videoSrc="ID"
            modalUploadInstruction={
              <ul style={{marginBottom: 0, paddingInlineStart: "2.4rem"}}>
                <li>
                  Acceptable documents include: valid government issued IDs such as a{" "}
                  <b>driver’s license</b>, <b>national identity card</b>, or an{" "}
                  <b>international passport</b>.
                </li>
                <li>
                  Use only original documents (no photocopies), placed on a flat surface
                  or held steadily.
                </li>
                <li>
                  Ensure the entire ID is visible, not cropped, and details are clear (no
                  blurs).
                </li>
                <li>
                  Take the photo in a well-lit area, avoiding glare, shadows, or poor
                  lighting.
                </li>
              </ul>
            }
            required
          />
          <CreateKycDocUploadFormField
            defaultPreviewUrl={kycStatusState.kycStatusData?.data.selfieWithIdUrl}
            name="selfieWithId"
            label="Selfie with your ID"
            btnText="Upload a selfie"
            docFeedback="We will compare your selfie with the photo on your government-issued ID to confirm your identity."
            uploadInstruction="Please upload a clear selfie of yourself holding your government-issued ID"
            modalTitle="A selfie with your ID"
            videoSrc="Selfie + ID"
            modalUploadInstruction={
              <ul style={{marginBottom: 0, paddingInlineStart: "2.4rem"}}>
                <li>
                  Ensure your face and ID are clearly visible, with the ID held next to
                  your face (not covering it).
                </li>
                <li>
                  Use the same ID as the one you uploaded, and make sure the details (like
                  name and photo) are readable.
                </li>
                <li>Take the photo in a well-lit area, avoiding glare or shadows.</li>
                <li>Do not wear hats, sunglasses, or face coverings during the photo.</li>
              </ul>
            }
            required
          />
          <CreateKycDocUploadFormField
            defaultPreviewUrl={kycStatusState.kycStatusData?.data.businessRegistrationUrl}
            name="businessRegistration"
            label="Business registration document (optional)"
            btnText="Upload a document"
            docFeedback=""
            uploadInstruction="We require an image of your business registration or certificate of incorporation to verify your business identity"
            modalTitle="Business registration document"
            modalUploadInstruction={
              <ul style={{marginBottom: 0, paddingInlineStart: "2.4rem"}}>
                <li>
                  Acceptable documents include:{" "}
                  <b>
                    CAC certificate, business name registration, certificate of
                    incorporation, any other government-issued business registration
                    document
                  </b>
                </li>
                <li>
                  Ensure the document shows your business name and registration number
                </li>
                <li>Take the photo in a well-lit area with no glare or shadows</li>
                <li>Avoid blurry and unclear images</li>
              </ul>
            }
            videoSrc="PoR"
          />
          <CreateKycDocUploadConfirmAccuracyFormField />

          <Row gutter={[0, 16]}>
            <Col span={24} sm={12}>
              <StyledServiceButtonWrapper>
                <Button
                  onClick={() =>
                    dispatch({
                      type: "update-create-kyc-form-step",
                      payload: "basicForm",
                    })
                  }
                  block
                  size="small"
                  type="default"
                >
                  Back
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  size="small"
                  disabled={
                    Object.keys(errors).length > 0 || Object.values(watch()).length < 1
                  }
                  loading={isSubmitting}
                >
                  Submit
                </Button>
              </StyledServiceButtonWrapper>
            </Col>
          </Row>
        </Space>
      </form>
      <Modal
        visible={isSuccessModalOpen}
        title={"Submission received"}
        onOk={handleCloseSuccessModal}
        onCancel={handleCloseSuccessModal}
        footer={null}
        centered
      >
        <StyledModalContentWrapper>
          <Space size={24} direction="vertical" style={{display: "flex"}}>
            <Space size={16} direction="vertical" style={{display: "flex"}}>
              <StyledDocUploadVideoWrapper>
                <StyledKycStatusRiveWrapper>
                  <RiveComponent />
                </StyledKycStatusRiveWrapper>
              </StyledDocUploadVideoWrapper>
              <div>
                <p>
                  Thank you. <br /> We’ve received your details and documents. Our team is
                  reviewing your submission for verification. This usually takes 24–48
                  hours. You’ll get an email notification once your account is verified.
                </p>
              </div>
            </Space>
          </Space>
        </StyledModalContentWrapper>
      </Modal>
    </FormProvider>
  );
}
